import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { updateIsFollowingAuthorForAuthor } from '../../api/followCacheHelpers';
import { useAuth } from '../../context/AuthContext';
import {
  FOLLOW_USER,
  UNFOLLOW_USER,
} from '../../graphql/resolvers/followMutations/followUser.gql';
import { GET_HOVERCARD_PROFILE } from './hovercardProfileQuery';

export interface HovercardProfile {
  userId: string;
  username: string;
  displayName: string;
  avatar: string | null;
  bio: string | null;
  followerCount: number;
  followingCount: number;
  isFollowedByViewer: boolean;
  reputationOverall: number | null;
  reputationVouchBoost: number;
  reputationDomains: string[];
}

export type HovercardState = {
  visible: boolean;
  loading: boolean;
  profile: HovercardProfile | null;
  error: boolean;
};

export type HovercardPosition = {
  top: number;
  left: number;
};

// Module-level cache — persists for the session, shared across all hook instances.
// Keys are `viewerId:profileUserId` so each viewer has an independent follow-state entry.
const profileCache = new Map<string, HovercardProfile>();
const inflightRequests = new Map<string, Promise<HovercardProfile>>();

function ck(viewerId: string | null, profileUserId: string): string {
  return `${viewerId ?? 'anon'}:${profileUserId}`;
}

const SHOW_DELAY = 300;
const DISMISS_DELAY = 150;

const HIDDEN_STATE: HovercardState = {
  visible: false,
  loading: false,
  profile: null,
  error: false,
};

export function useHovercard(initialData?: HovercardProfile) {
  const apolloClient = useApolloClient();
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;

  const [state, setState] = useState<HovercardState>(HIDDEN_STATE);
  const [position, setPosition] = useState<HovercardPosition>({
    top: 0,
    left: 0,
  });

  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeUserIdRef = useRef<string | null>(null);

  const [followMutation] = useMutation(FOLLOW_USER);
  const [unfollowMutation] = useMutation(UNFOLLOW_USER);

  const cancelDismiss = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    cancelDismiss();
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    activeUserIdRef.current = null;
    setState(HIDDEN_STATE);
  }, [cancelDismiss]);

  const scheduleDismiss = useCallback(() => {
    cancelDismiss();
    dismissTimerRef.current = setTimeout(hide, DISMISS_DELAY);
  }, [cancelDismiss, hide]);

  const fetchProfile = useCallback(
    async (userId: string): Promise<HovercardProfile> => {
      const key = ck(currentUserId, userId);
      if (profileCache.has(key)) {
        const cached = profileCache.get(key);
        if (cached) return cached;
        profileCache.delete(key); // evict stale entry from a previous failed fetch
      }
      if (inflightRequests.has(key)) {
        return inflightRequests.get(key) as Promise<HovercardProfile>;
      }
      const promise = apolloClient
        .query({
          query: GET_HOVERCARD_PROFILE,
          variables: { userId },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          const profile = res.data?.getHovercardProfile;
          // errorPolicy:'all' means GraphQL errors don't throw — guard explicitly.
          if (!profile) {
            throw new Error(
              res.errors?.[0]?.message ??
                'getHovercardProfile returned no data',
            );
          }
          profileCache.set(key, profile as HovercardProfile);
          return profile as HovercardProfile;
        })
        .finally(() => {
          inflightRequests.delete(key);
        });
      inflightRequests.set(key, promise);
      return promise;
    },
    [apolloClient, currentUserId],
  );

  const showForUser = useCallback(
    (userId: string, anchorEl: HTMLElement) => {
      const rect = anchorEl.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: Math.min(rect.left + window.scrollX, window.innerWidth - 320 - 8),
      });

      if (initialData && initialData.userId === userId) {
        profileCache.set(ck(currentUserId, userId), initialData);
      }

      const cachedProfile = profileCache.get(ck(currentUserId, userId));
      if (cachedProfile) {
        setState({
          visible: true,
          loading: false,
          profile: cachedProfile,
          error: false,
        });
        return;
      }

      setState({ visible: true, loading: true, profile: null, error: false });

      fetchProfile(userId)
        .then((profile) => {
          if (activeUserIdRef.current === userId) {
            setState({ visible: true, loading: false, profile, error: false });
          }
        })
        .catch(() => {
          if (activeUserIdRef.current === userId) {
            setState({
              visible: true,
              loading: false,
              profile: null,
              error: true,
            });
          }
        });
    },
    [fetchProfile, initialData, currentUserId],
  );

  // Clear viewer-specific cached data whenever the logged-in user changes so a
  // newly logged-in user never sees a previous viewer's isFollowedByViewer state.
  useEffect(() => {
    profileCache.clear();
    inflightRequests.clear();
  }, [currentUserId]);

  // Stable refs so the event listeners (registered once) always read current values
  // without needing to re-register on every render.
  const currentUserIdRef = useRef(currentUserId);
  const showForUserRef = useRef(showForUser);
  const cancelDismissRef = useRef(cancelDismiss);
  const scheduleDismissRef = useRef(scheduleDismiss);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);
  useEffect(() => {
    showForUserRef.current = showForUser;
  }, [showForUser]);
  useEffect(() => {
    cancelDismissRef.current = cancelDismiss;
  }, [cancelDismiss]);
  useEffect(() => {
    scheduleDismissRef.current = scheduleDismiss;
  }, [scheduleDismiss]);

  // Document-level event delegation for avatar triggers.
  // Uses a single cancellable dismiss timer shared with the card's own handlers,
  // which eliminates the gap-crossing dismissal bug.
  // Registered once (empty deps) — current values accessed via refs above.
  useEffect(() => {
    const handleMouseEnter = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target?.closest) return;
      if (target.closest('.tex-hovercard')) return;

      const avatarEl = target.closest('[data-user-id]') as HTMLElement | null;
      if (!avatarEl) return;

      const userId = avatarEl.dataset.userId;
      if (!userId || userId === currentUserIdRef.current) return;

      cancelDismissRef.current();

      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }

      activeUserIdRef.current = userId;

      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = null; // mark as fired so mouseleave knows the card is live
        if (activeUserIdRef.current === userId) {
          showForUserRef.current(userId, avatarEl);
        }
      }, SHOW_DELAY);
    };

    const handleMouseLeave = (e: Event) => {
      const target = e.target as HTMLElement;
      const relatedTarget = (e as MouseEvent)
        .relatedTarget as HTMLElement | null;

      const leavingAvatar = !!target?.closest?.('[data-user-id]');
      const leavingCard = !!target?.closest?.('.tex-hovercard');
      if (!leavingAvatar && !leavingCard) return;

      // Cancel pending show if leaving avatar before the card has appeared.
      // showTimerRef.current is non-null only while the show delay is still pending;
      // it is cleared to null the moment the timer fires (card becomes live).
      if (leavingAvatar && showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
        activeUserIdRef.current = null;
        return;
      }

      if (relatedTarget?.closest?.('.tex-hovercard')) return;
      if (relatedTarget?.closest?.('[data-user-id]')) return;

      scheduleDismissRef.current();
    };

    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);

    return () => {
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
    };
  }, []);

  // username is passed (not UUID) so the server writes SK: FOLLOWS#<username>,
  // matching the key format used by the feed query's followSet lookup.
  const onFollow = useCallback(
    async (username: string) => {
      const res = await followMutation({
        variables: { input: { userId: username } },
      });
      const data = res.data?.followUser;
      if (!data) return;
      setState((prev) => {
        if (!prev.profile || prev.profile.username !== username) return prev;
        const updated = {
          ...prev.profile,
          isFollowedByViewer: data.isFollowing as boolean,
          followerCount: data.followerCount as number,
        };
        profileCache.set(ck(currentUserId, prev.profile.userId), updated);
        return { ...prev, profile: updated };
      });
      updateIsFollowingAuthorForAuthor(apolloClient.cache, username, true);
    },
    [followMutation, currentUserId, apolloClient],
  );

  const onUnfollow = useCallback(
    async (username: string) => {
      const res = await unfollowMutation({
        variables: { input: { userId: username } },
      });
      const data = res.data?.unfollowUser;
      if (!data) return;
      setState((prev) => {
        if (!prev.profile || prev.profile.username !== username) return prev;
        const updated = {
          ...prev.profile,
          isFollowedByViewer: data.isFollowing as boolean,
          followerCount: data.followerCount as number,
        };
        profileCache.set(ck(currentUserId, prev.profile.userId), updated);
        return { ...prev, profile: updated };
      });
      updateIsFollowingAuthorForAuthor(apolloClient.cache, username, false);
    },
    [unfollowMutation, currentUserId, apolloClient],
  );

  // Handlers applied to the hovercard element — cancel/restart the shared dismiss timer,
  // forming a compound hover zone with the avatar trigger.
  const cardHandlers = {
    onMouseEnter: cancelDismiss,
    onMouseLeave: scheduleDismiss,
  };

  return { state, position, cardHandlers, onFollow, onUnfollow, hide };
}
